import type { OpaqueVideoData } from '@lumen5/luminary';
import Luminary from '@lumen5/luminary';

import {
    captureVideoState,
    computeVideoDiff,
    formatInitialStateAsGitDiff,
    formatVideoDiffAsGitPatch,
} from './diff';
import type { VideoStateSnapshot } from './types';

import { IconRegistry } from 'js/src/agent/registries/IconRegistry';
import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { MusicRegistry } from 'js/src/agent/registries/MusicRegistry';
import type { MediaCandidates } from 'js/src/client';

/**
 * Options for capturing video state context
 */
export type CaptureVideoStateContextOptions = {
    mediaCandidates?: MediaCandidates | null;
    previousState?: VideoStateSnapshot | null;
    useDiffMode?: boolean;
};

/**
 * Result of capturing video state context
 */
export type CaptureVideoStateContextResult = {
    description: string;
    snapshot: VideoStateSnapshot;
    isDiff: boolean;
    hasChanges: boolean;
};

/**
 * Captures the current video state and generates a text-based description for agent context.
 * @param luminary The Luminary instance containing video data
 * @param options Configuration options including media candidates and previous state for diff mode
 */
export async function captureVideoStateContext(
    luminary: Luminary,
    options?: CaptureVideoStateContextOptions,
): Promise<CaptureVideoStateContextResult> {
    const { mediaCandidates, previousState, useDiffMode = true } = options || {};

    // Ensure all assets from the video are in the registry
    const mediaRegistry = MediaRegistry.getInstance();
    await mediaRegistry.addAnyUnseenItemsFromVideo(luminary);
    const iconRegistry = IconRegistry.getInstance();
    await iconRegistry.addAnyUnseenItemsFromVideo(luminary);
    const musicRegistry = MusicRegistry.getInstance();
    await musicRegistry.addAnyUnseenItemsFromVideo(luminary);

    // Add media candidates from recommendations to registries
    if (mediaCandidates) {
        await mediaRegistry.addCandidatesFromRecommendations(mediaCandidates);
        await iconRegistry.addCandidatesFromRecommendations(mediaCandidates);
    }

    // Capture current state
    const currentSnapshot = await captureVideoState(luminary, mediaCandidates);

    // Generate diff if previous state exists and diff mode is enabled
    if (useDiffMode && previousState) {
        const diff = computeVideoDiff(previousState, currentSnapshot);
        const gitDiff = formatVideoDiffAsGitPatch(diff);
        const hasChanges = gitDiff !== '';
        const diffDescription = formatVideoDiff(diff);

        return {
            description: diffDescription,
            snapshot: currentSnapshot,
            isDiff: true,
            hasChanges,
        };
    }

    // Generate full description using git diff format (first call or diff mode disabled)
    let description = '## Video Context\n';
    description += '_This is the complete video state_\n\n';

    // Use git diff format for initial state
    description += formatInitialStateAsGitDiff(currentSnapshot);

    return {
        description,
        snapshot: currentSnapshot,
        isDiff: false,
        hasChanges: true, // Initial full state always has "changes" to report
    };
}

/**
 * Format a video diff into a human-readable description using git patch format
 */
function formatVideoDiff(diff: ReturnType<typeof computeVideoDiff>): string {
    let description = '## Video Context\n';
    description += `_This is an incremental update showing only changes since ${new Date(diff.baselineTimestamp).toISOString()}_\n\n`;

    // Use git patch format for the diff
    const gitDiff = formatVideoDiffAsGitPatch(diff);

    if (gitDiff) {
        description += gitDiff;
    } else {
        description += '_(No changes detected)_\n';
    }

    return description;
}

/**
 * Generates a comprehensive video description from video data by creating a temporary Luminary instance.
 * This is useful for analyzing videos without having an existing Luminary instance.
 * @param videoData The video data to analyze
 * @returns A formatted string description of the video including metadata, music, and scene details
 */
export async function generateVideoDescriptionFromVideoData(
    videoData: OpaqueVideoData,
): Promise<string> {
    // Create temporary Luminary instance for analysis
    const localLuminary = new Luminary({
        fetchResources: true,
        disableRenderer: true,
        autoDraw: true,
        autoUpdate: true,
    });

    try {
        localLuminary.load(videoData);
        await localLuminary.onAssetsLoaded();

        const result = await captureVideoStateContext(localLuminary);
        return result.description;
    } finally {
        // Always clean up the local Luminary instance
        localLuminary.destroy();
    }
}
